import {
    Directive,
    EmbeddedViewRef,
    IterableChangeRecord,
    IterableDiffer,
    IterableDiffers,
    NgIterable,
    NgZone,
    TemplateRef,
    TrackByFunction,
    ViewContainerRef,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    untracked,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { Grid } from "./s25.virtual.grid";
import { GridUtil } from "./s25.virtual.grid.util";
import { S25ScrollUtil } from "../../standalone/s25-scroll-minimap/s25.scroll.util";

const trackByKey: TrackByFunction<{ key: string }> = (_, entry) => entry.key;

@Directive({
    selector: "[virtualGrid][virtualGridOf]",
    standalone: true,
})
export class S25VirtualGridDirective<Entry extends Grid.RenderEntry, Iter extends NgIterable<Entry> = Entry[]>
    implements OnDestroy
{
    readonly virtualGridOf = input.required<Iter | undefined | null>();
    readonly virtualGridTrackBy = input<TrackByFunction<Entry> | undefined>(trackByKey);
    readonly virtualGridRows = input.required({
        transform: (rows: number | undefined) => rows ?? 100,
    });
    readonly virtualGridColumns = input.required({
        transform: (columns: number | undefined) => columns ?? 100,
    });
    readonly virtualGridScrollElementX = input.required<HTMLElement>();
    readonly virtualGridScrollElementY = input.required<HTMLElement>();
    readonly virtualGridWindowElement = input.required<HTMLElement>();
    /** Skip vertical windowing. Frozen rows are pinned, so document scroll does not describe them. */
    readonly virtualGridFullY = input(false);
    readonly virtualGridTemplate = input<TemplateRef<VirtualGridOfContext<Entry, Iter>>>();

    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly implicitTemplateRef = inject<TemplateRef<VirtualGridOfContext<Entry, Iter>>>(TemplateRef);
    private readonly differs = inject(IterableDiffers);
    private readonly zone = inject(NgZone);

    private readonly templateRef = computed(() => this.virtualGridTemplate() ?? this.implicitTemplateRef);
    private readonly index = computed(() => {
        const list = this.virtualGridOf();
        const rows = this.virtualGridRows();
        const columns = this.virtualGridColumns();
        if (!list || !rows || !columns) return new Map() as Grid.ItemIndex<Entry>;
        // Snapshot positions so drag/move updates do not rebuild the spatial index.
        return untracked(() => GridUtil.createItemIndex(list, rows, columns, (entry) => entry.clippedPosition()));
    });

    private visibleDiffer: IterableDiffer<Entry> | null = null;

    constructor() {
        effect((onCleanup) => {
            const list = this.virtualGridOf();
            const rows = this.virtualGridRows();
            const columns = this.virtualGridColumns();
            const scrollElementX = this.virtualGridScrollElementX();
            const scrollElementY = this.virtualGridScrollElementY();
            const windowElement = this.virtualGridWindowElement();
            if (!rows || !columns || !list || !scrollElementX || !scrollElementY || !windowElement) return;

            this.zone.runOutsideAngular(() => {
                document.addEventListener("scroll", this.render, true);
            });
            const resizeObserver = new ResizeObserver(this.render);
            for (const child of elementChildren(scrollElementX)) resizeObserver.observe(child);
            for (const child of elementChildren(scrollElementY)) resizeObserver.observe(child);

            untracked(() => this.render());

            onCleanup(() => {
                document.removeEventListener("scroll", this.render, true);
                resizeObserver.disconnect();
            });
        });
    }

    private readonly render = S25Util.throttled(() => this.renderNow(), 10, true);
    // ANG-7906: The throttled render can fire after the view container is cleared.
    private destroyed = false;

    ngOnDestroy() {
        this.destroyed = true;
    }

    private renderNow() {
        if (this.destroyed) return;
        const list = this.virtualGridOf();
        const rows = this.virtualGridRows();
        const columns = this.virtualGridColumns();
        const scrollElementX = this.virtualGridScrollElementX();
        const scrollElementY = this.virtualGridScrollElementY();
        const windowElement = this.virtualGridWindowElement();
        if (!rows || !columns || !list || !scrollElementX || !scrollElementY || !windowElement) return;

        const { offsetBottom, offsetRight, offsetTop, offsetLeft } = S25ScrollUtil.getScrollLimit(
            scrollElementX,
            scrollElementY,
            windowElement,
        );

        const scrollHeight = scrollElementY.scrollHeight - offsetTop - offsetBottom;
        const scrollWidth = scrollElementX.scrollWidth - offsetLeft - offsetRight;
        const topFraction = S25Util.clamp((scrollElementY.scrollTop - offsetTop) / scrollHeight, 0, 1);
        const leftFraction = S25Util.clamp((scrollElementX.scrollLeft - offsetLeft) / scrollWidth, 0, 1);
        let heightFraction = Math.min(windowElement.clientHeight, window.innerHeight) / scrollHeight;
        heightFraction = S25Util.clamp(heightFraction, 0, 1);
        let widthFraction = Math.min(windowElement.clientWidth, window.innerWidth) / scrollWidth;
        widthFraction = S25Util.clamp(widthFraction, 0, 1);

        const firstRow = this.virtualGridFullY() ? 0 : Math.floor(topFraction * rows);
        const firstColumn = Math.floor(leftFraction * columns);
        const lastRow = this.virtualGridFullY() ? rows : Math.ceil((topFraction + heightFraction) * rows);
        const lastColumn = Math.ceil((leftFraction + widthFraction) * columns);

        const visibleItems = GridUtil.getItemsInRegion(
            this.index(),
            firstRow,
            lastRow,
            firstColumn,
            lastColumn,
            (entry) => entry.key,
        );
        const visibleItemsSet = new Set(visibleItems);

        // Always include items being moved
        for (const entry of list) {
            if (visibleItemsSet.has(entry)) continue;
            if (entry.dragging() || entry.moving()) visibleItems.push(entry);
        }
        this.queueRender(visibleItems);
    }

    private rendering = false;
    private renderQueue: Entry[][] = [];
    private queueRender(items: Entry[]) {
        this.renderQueue.push(items);
        if (this.rendering) return;

        this.rendering = true;
        while (this.renderQueue.length > 0) {
            this.applyVisibleItems(this.renderQueue.shift()!);
        }
        this.rendering = false;
    }

    private applyVisibleItems(visibleItems: Entry[]) {
        this.visibleDiffer ??= this.differs
            .find([])
            .create(this.virtualGridTrackBy() ?? (trackByKey as TrackByFunction<Entry>));
        const changes = this.visibleDiffer.diff(visibleItems);
        changes?.forEachOperation((change, adjustedPreviousIndex: number | null, currentIndex: number | null) => {
            if (change.previousIndex == null) {
                this.viewContainerRef.createEmbeddedView(
                    this.templateRef(),
                    new VirtualGridOfContext<Entry, Iter>(change.item, this.virtualGridOf()!, -1, -1),
                    currentIndex === null ? undefined : currentIndex,
                );
                return;
            }
            const view = adjustedPreviousIndex === null ? null : this.viewContainerRef.get(adjustedPreviousIndex);
            if (!view) {
                throw new Error(
                    `Virtual Grid: view container out of step with its differ at index ${adjustedPreviousIndex}`,
                );
            }
            if (currentIndex == null) {
                this.viewContainerRef.remove(adjustedPreviousIndex!);
                return;
            }
            this.viewContainerRef.move(view, currentIndex);
            applyViewChange(view as EmbeddedViewRef<VirtualGridOfContext<Entry, Iter>>, change);
        });
        changes?.forEachIdentityChange((change) => {
            if (change.currentIndex == null) return;
            const view = this.viewContainerRef.get(change.currentIndex);
            if (!view) return;
            applyViewChange(view as EmbeddedViewRef<VirtualGridOfContext<Entry, Iter>>, change);
        });
    }

    /**
     * Renders every virtual item regardless of viewport size (for testing purposes)
     */
    renderAll() {
        this.viewContainerRef.clear();
        for (const item of this.virtualGridOf() ?? []) {
            this.viewContainerRef.createEmbeddedView(
                this.templateRef(),
                new VirtualGridOfContext<Entry, Iter>(item, this.virtualGridOf()!, -1, -1),
            );
        }
    }

    refresh() {
        this.renderNow();
    }

    static ngTemplateContextGuard<Entry extends Grid.RenderEntry, Iter extends NgIterable<Entry> = Entry[]>(
        directive: S25VirtualGridDirective<Entry, Entry[]>,
        context: unknown,
    ): context is VirtualGridOfContext<Entry, Iter> {
        return true;
    }
}

function elementChildren(element: HTMLElement): HTMLElement[] {
    return Array.from(element.children) as HTMLElement[];
}

function applyViewChange<Entry extends Grid.RenderEntry, Iter extends NgIterable<Entry> = Entry[]>(
    view: EmbeddedViewRef<VirtualGridOfContext<Entry, Iter>>,
    record: IterableChangeRecord<Entry>,
) {
    view.context.$implicit = record.item;
    view.detectChanges();
}

export class VirtualGridOfContext<Entry extends Grid.RenderEntry, Iter extends NgIterable<Entry> = Entry[]> {
    constructor(
        public $implicit: Entry,
        public virtualGridOf: Iter,
        public index: number,
        public count: number,
    ) {}
}
